import {
  isBenignAuthSignOutError,
  signOutClientSession,
} from './authSignOut';
import { clearPlatformAccessSessionMarkers } from '../lib/platformAccessEvents';

const CACHE_KEY = 'academyAccessCache';
const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes
export const ACADEMY_SKIP_AUTO_REDIRECT_KEY = 'academy_skip_auto_redirect';

const safeWindow = () => (typeof window !== 'undefined' ? window : null);
const safeStorage = () => {
  const win = safeWindow();
  return win ? win.localStorage : null;
};

export const getCachedAcademyAccess = () => {
  const storage = safeStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
      storage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Failed to read academy access cache', error);
    storage.removeItem(CACHE_KEY);
    return null;
  }
};

export const setCachedAcademyAccess = ({ academyName, coachName, userId }) => {
  const storage = safeStorage();
  if (!storage) return;

  const payload = {
    academyName,
    coachName,
    userId,
    expiresAt: Date.now() + CACHE_DURATION_MS,
  };

  storage.setItem(CACHE_KEY, JSON.stringify(payload));
};

export const clearCachedAcademyAccess = () => {
  const storage = safeStorage();
  if (!storage) return;

  storage.removeItem(CACHE_KEY);
};

export const persistAcademyContext = ({ academyName, coachName, userId }) => {
  const normalizedAcademyName = academyName || 'Academy';
  const normalizedCoachName = coachName || 'Coach';

  const storage = safeStorage();
  if (!storage) return;

  storage.setItem('academyName', normalizedAcademyName);
  storage.setItem('coachName', normalizedCoachName);

  setCachedAcademyAccess({
    academyName: normalizedAcademyName,
    coachName: normalizedCoachName,
    userId,
  });
};

export const clearAcademyContext = () => {
  const storage = safeStorage();
  if (!storage) return;

  const cachedUserId = getCachedAcademyAccess()?.userId ?? null;
  storage.removeItem('academyName');
  storage.removeItem('coachName');
  clearCachedAcademyAccess();
  clearPlatformAccessSessionMarkers('academy', cachedUserId);
};

export const clearAcademySkipAutoRedirect = () => {
  const win = safeWindow();
  if (!win) return;
  try {
    win.sessionStorage.removeItem(ACADEMY_SKIP_AUTO_REDIRECT_KEY);
  } catch {
    /* ignore */
  }
};

/** Sign out of Supabase and clear academy portal client state. */
export async function signOutAcademyPortal() {
  const win = safeWindow();
  if (win) {
    try {
      win.sessionStorage.setItem(ACADEMY_SKIP_AUTO_REDIRECT_KEY, '1');
      win.sessionStorage.removeItem('academy_signup_flow');
    } catch {
      /* ignore */
    }
  }

  clearAcademyContext();

  try {
    await signOutClientSession();
  } catch (error) {
    if (!isBenignAuthSignOutError(error)) {
      clearAcademySkipAutoRedirect();
      throw error;
    }
  }
}

